import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { BarChart3, Users, Award, CheckCircle2, Shield, TrendingUp, User, Building2, AlertTriangle, Printer, Calendar, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import StatCard from '@/components/dashboard/StatCard';
import ComplianceHeatmap from '@/components/reports/ComplianceHeatmap';
import QuarterlyTrendChart from '@/components/reports/QuarterlyTrendChart';

const PERIOD_OPTIONS = [
  { value: '7', label: 'Son 7 gün' },
  { value: '30', label: 'Son 30 gün' },
  { value: '90', label: 'Son 90 gün' },
  { value: '365', label: 'Son 1 yıl' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

export default function ExecutiveDashboardPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [periodDays, setPeriodDays] = useState('30');
  const [expandedReg, setExpandedReg] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, repRes] = await Promise.all([
        fetch(`${API_URL}/api/executive/dashboard?days=${periodDays}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/executive/regulatory-report?report_type=summary`, { headers: getAuthHeaders() })
      ]);
      if (dashRes.ok) setData(await dashRes.json());
      if (repRes.ok) setReport(await repRes.json());
    } catch (err) {
      toast.error('Veriler yüklenirken hata');
    } finally {
      setLoading(false);
    }
  }, [periodDays]);

  useEffect(() => { loadData(); }, [loadData]);

  const exportPdf = () => {
    // Tarayıcı Yazdır diyaloğu açılır; print-area CSS'i ile sidebar/footer
    // gizlenir, A4 üzerinde skorkart + departman raporu sığar.
    window.print();
  };

  const getReadinessLabel = (r) => r === 'high' ? 'Yüksek' : r === 'medium' ? 'Orta' : 'Düşük';
  const getReadinessStatCardColor = (r) => r === 'high' ? 'emerald' : r === 'medium' ? 'amber' : 'red';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BarChart3 className="w-10 h-10 text-primary animate-pulse" />
          <p className="text-muted-foreground text-sm">Kurumsal veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  const scorecard = data?.scorecard || {};
  const summary = data?.summary || {};

  const currentPeriodLabel = PERIOD_OPTIONS.find(o => o.value === periodDays)?.label || 'Son 30 gün';

  return (
    <div className="print-area" data-testid="executive-dashboard">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .no-print { display: none !important; }
          .print-break-inside-avoid { break-inside: avoid !important; page-break-inside: avoid !important; }
          html, body { background: #fff !important; }
        }
      `}</style>
      <div className="no-print">
        <DashboardHeader
          icon={BarChart3}
          title="Üst Yönetim Paneli"
          subtitle={`Kurumsal Uyumluluk Skorkartı — ${currentPeriodLabel}`}
          onRefresh={loadData}
          actions={<Badge variant="outline"><User className="w-3 h-3 mr-1" />{currentUser?.name}</Badge>}
        />
      </div>

      <main className="container mx-auto px-4 py-6 max-w-7xl space-y-6">
        {/* Filtre + Export bar */}
        <div className="flex flex-wrap items-center gap-3 no-print" data-testid="executive-controls">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Dönem:</span>
            <Select value={periodDays} onValueChange={setPeriodDays}>
              <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="executive-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportPdf}
            className="ml-auto"
            data-testid="executive-pdf-btn"
          >
            <Printer className="w-4 h-4 mr-1" /> PDF Olarak İndir
          </Button>
        </div>
        {/* Yazdırıldığında rapor başlığı */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold">Üst Yönetim Uyum Raporu</h1>
          <p className="text-sm text-slate-600">Dönem: {currentPeriodLabel} — Hazırlayan: {currentUser?.name}</p>
          <p className="text-xs text-slate-500">Oluşturulma: {new Date().toLocaleString('tr-TR')}</p>
        </div>

        {/* Scorecard */}
        <div id="skorkart" className="grid grid-cols-2 md:grid-cols-4 gap-4 scroll-mt-20" data-testid="executive-scorecard">
          <StatCard
            variant="center"
            icon={Shield}
            color={getReadinessStatCardColor(scorecard.regulatory_readiness)}
            value={getReadinessLabel(scorecard.regulatory_readiness)}
            label="Regülatör Hazırlık"
          />
          <StatCard variant="center" icon={CheckCircle2} color="primary" value={`%${scorecard.compliance_score || 0}`} label="Uyumluluk Skoru" />
          <StatCard variant="center" icon={TrendingUp} color="blue" value={`%${scorecard.avg_completion || 0}`} label="Ort. Tamamlanma" />
          <StatCard variant="center" icon={Award} color="amber" value={`%${scorecard.avg_quiz_score || 0}`} label="Ort. Quiz Başarısı" />
        </div>

        {/* Summary + Regulatory Report */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Kurumsal Özet</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border rounded-lg text-center">
                  <div className="text-2xl font-bold">{summary.total_staff || 0}</div>
                  <div className="text-xs text-muted-foreground">Toplam Personel</div>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <div className="text-2xl font-bold">{summary.total_modules || 0}</div>
                  <div className="text-xs text-muted-foreground">Eğitim Modülü</div>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <div className="text-2xl font-bold">{summary.total_lessons || 0}</div>
                  <div className="text-xs text-muted-foreground">Ders</div>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <div className="text-2xl font-bold">{summary.total_certificates || 0}</div>
                  <div className="text-xs text-muted-foreground">Sertifika</div>
                </div>
              </div>
              {summary.trends && (
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm font-medium mb-2">{currentPeriodLabel} Trendleri</div>
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-emerald-500">
                        {summary.trends?.recent_completions ?? summary.trends?.recent_completions_30d ?? 0}
                      </div>
                      <div className="text-[10px] text-muted-foreground">Tamamlanan Ders</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-amber-500">
                        {summary.trends?.recent_certificates ?? summary.trends?.recent_certificates_30d ?? 0}
                      </div>
                      <div className="text-[10px] text-muted-foreground">Yeni Sertifika</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Regulatory Report */}
          <Card id="regulasyon" className="scroll-mt-20">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" />Regülatör Uyum Raporu</CardTitle></CardHeader>
            <CardContent>
              {report?.regulations ? (
                <div className="space-y-3">
                  {report.regulations.map((reg) => {
                    const expanded = expandedReg === reg.key;
                    const hasModules = Array.isArray(reg.related_modules) && reg.related_modules.length > 0;
                    return (
                      <div
                        key={reg.key || reg.name}
                        className={`rounded-lg border ${reg.status === 'compliant' ? 'bg-emerald-500/5 border-emerald-500/20' : reg.status === 'partial' ? 'bg-amber-500/5 border-amber-500/20' : 'bg-red-500/5 border-red-500/20'}`}
                      >
                        <button
                          type="button"
                          onClick={() => hasModules && setExpandedReg(expanded ? null : reg.key)}
                          className={`w-full p-3 text-left ${hasModules ? 'cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.02]' : 'cursor-default'}`}
                          data-testid={`regulation-${reg.key}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {hasModules && (
                                expanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                              )}
                              <span className="text-sm font-medium">{reg.name}</span>
                            </div>
                            <Badge className={reg.status === 'compliant' ? 'bg-emerald-600' : reg.status === 'partial' ? 'bg-amber-600' : 'bg-red-600'}>{reg.status === 'compliant' ? 'Uyumlu' : reg.status === 'partial' ? 'Kısmi' : 'Uyumsuz'}</Badge>
                          </div>
                          <Progress value={reg.compliance_rate || 0} className="h-1.5" />
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] text-muted-foreground">%{reg.compliance_rate || 0} tamamlanma</span>
                            {reg.requirement && (
                              <span className="text-[10px] text-muted-foreground italic truncate max-w-[60%]">{reg.requirement}</span>
                            )}
                          </div>
                        </button>
                        {expanded && hasModules && (
                          <div className="px-3 pb-3 pt-0 space-y-1.5 border-t border-border/50">
                            <div className="text-[11px] font-medium text-muted-foreground pt-2">İlgili Modüller</div>
                            {reg.related_modules.map((m) => (
                              <div key={m.module_id} className="flex items-center gap-2 text-xs bg-background/40 rounded px-2 py-1.5">
                                <span className="font-mono text-muted-foreground w-12 shrink-0">{m.module_id}</span>
                                <span className="flex-1 truncate">{m.module_name}</span>
                                <span className="text-muted-foreground shrink-0">{m.lesson_count} ders</span>
                                <span className={`font-bold shrink-0 w-12 text-right ${m.completion_rate >= 80 ? 'text-emerald-500' : m.completion_rate >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
                                  %{m.completion_rate}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">Rapor verisi yüklenemedi</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Çeyreklik trend */}
        <QuarterlyTrendChart quarters={4} />

        {/* Compliance Heatmap */}
        <ComplianceHeatmap />

        {/* Department Rankings */}
        <div id="departman-skorlari" className="grid grid-cols-1 lg:grid-cols-2 gap-6 scroll-mt-20">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-500" />En Başarılı Departmanlar</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(data?.top_departments || []).map((dept, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-emerald-500/5">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-xs font-bold text-emerald-500">{i + 1}</div>
                    <div className="flex-1 text-sm">{dept.department}</div>
                    <div className="text-sm font-bold text-emerald-500">%{dept.avg_completion || 0}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" />Geliştirilmesi Gereken</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(data?.bottom_departments || []).map((dept, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-red-500/5">
                    <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center text-xs font-bold text-red-500">{i + 1}</div>
                    <div className="flex-1 text-sm">{dept.department}</div>
                    <div className="text-sm font-bold text-red-500">%{dept.avg_completion || 0}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
